import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Plus, X, Loader2, Save, ShieldAlert, Quote } from 'lucide-react';
import { api } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { ScoreRing, StraightLineBars } from './CoachScore';
import { useCoachT } from '../../lib/coachI18n';
import { copyText } from '../../lib/coachApi';

const copy = async (text, t) => {
  if (await copyText(text)) toast.success(t('copied'));
  else toast.error(t('error'));
};

const Section = ({ title, children, testid }) => (
  <div className="space-y-1.5" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">{title}</div>
    {children}
  </div>
);

const Bullets = ({ items, accent }) => (
  <ul className="space-y-1">
    {(items || []).map((s, i) => (
      <li key={i} className="text-sm flex gap-2"><span style={{ color: accent }}>●</span><span>{s}</span></li>
    ))}
  </ul>
);

const StepsEditor = ({ steps, setSteps, t }) => (
  <div className="space-y-1.5" data-testid="coach-review-steps">
    {steps.map((s, i) => (
      <div key={i} className="flex items-center gap-2">
        <Checkbox checked={s.on} onCheckedChange={(v) => setSteps(steps.map((x, j) => (j === i ? { ...x, on: !!v } : x)))} data-testid={`coach-step-check-${i}`} />
        <Input value={s.text} onChange={(e) => setSteps(steps.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} className="h-8 text-sm" data-testid={`coach-step-input-${i}`} />
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setSteps(steps.filter((_, j) => j !== i))} data-testid={`coach-step-remove-${i}`}><X className="w-4 h-4" /></Button>
      </div>
    ))}
    <Button size="sm" variant="ghost" onClick={() => setSteps([...steps, { text: '', on: true }])} data-testid="coach-step-add"><Plus className="w-4 h-4 mr-1" />{t('addStep')}</Button>
  </div>
);

const Objections = ({ items, t }) => (
  <div className="space-y-2" data-testid="coach-review-objections">
    {items.map((o, i) => (
      <div key={i} className="rounded-md border border-border p-2.5 bg-[var(--surface-1)]">
        <div className="text-sm font-medium flex gap-1.5"><Quote className="w-3.5 h-3.5 mt-0.5 text-[#C9A227]" />{o.objection}</div>
        {o.how_handled && <div className="text-xs text-muted-foreground mt-1">{t('handled')}: {o.how_handled}</div>}
        {o.better_answer && <div className="text-sm mt-1.5 pl-2 border-l-2 border-[#1F7A4D]"><b>{t('better')}:</b> {o.better_answer}</div>}
      </div>
    ))}
  </div>
);

const Coaching = ({ a, t }) => (
  <>
    <div className="grid sm:grid-cols-2 gap-4">
      <Section title={t('strengths')} testid="coach-review-strengths"><Bullets items={a.strengths} accent="#1F7A4D" /></Section>
      <Section title={t('improvements')} testid="coach-review-improvements"><Bullets items={a.improvements} accent="#B4452F" /></Section>
    </div>
    {Object.keys(a.straight_line || {}).length > 0 && <Section title={t('straightLine')}><StraightLineBars data={a.straight_line} /></Section>}
    {(a.objections || []).length > 0 && <Section title={t('objections')}><Objections items={a.objections} t={t} /></Section>}
    <div className="grid sm:grid-cols-2 gap-3">
      {a.belfort_tip && <div className="rounded-md p-3 bg-[#0F2A22] text-[#F4EBD0] text-sm" data-testid="coach-belfort-tip"><div className="text-[10px] uppercase tracking-widest text-[#C9A227] mb-1">{t('belfort')}</div>{a.belfort_tip}</div>}
      {a.cardone_tip && <div className="rounded-md p-3 bg-[#2A1A0F] text-[#F4EBD0] text-sm" data-testid="coach-cardone-tip"><div className="text-[10px] uppercase tracking-widest text-[#E8A33D] mb-1">{t('cardone')}</div>{a.cardone_tip}</div>}
    </div>
  </>
);

/* Revisione analisi: l'agente controlla/modifica e salva nella scheda */
export const CoachReviewDialog = ({ call, onClose, onApplied }) => {
  const { t } = useCoachT();
  const a = call?.analysis || {};
  const [summary, setSummary] = useState('');
  const [steps, setSteps] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSummary(a.summary || '');
    setSteps((a.next_steps || []).map((text) => ({ text, on: true })));
  }, [call?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const applied = call?.status === 'applied';
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/coach/calls/${call.id}/apply`, { summary, next_steps: steps.filter((s) => s.on && s.text.trim()).map((s) => s.text) });
      toast.success(`${t('saved')} (${r.data.steps_added})`);
      onApplied?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!call} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="coach-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-4 text-left">
            {!call?.evaluation_hidden && <ScoreRing score={a.score || 0} />}
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#C9A227]">{t('score')}</div>
              <div className="font-serif text-lg leading-snug" data-testid="coach-review-verdict">{a.verdict || t('title')}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{call?.user_name} · {(call?.created_at || '').slice(0, 16).replace('T', ' ')}</div>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          {call?.evaluation_hidden && <div className="text-xs flex items-center gap-1.5 text-muted-foreground" data-testid="coach-eval-hidden"><ShieldAlert className="w-4 h-4" />{t('hiddenEval')}</div>}
          <Section title={t('summary')}><Textarea rows={6} value={summary} onChange={(e) => setSummary(e.target.value)} disabled={applied || call?.evaluation_hidden} data-testid="coach-review-summary" /></Section>
          {Object.keys(a.client_snapshot || {}).length > 0 && (
            <Section title={t('snapshot')}>
              <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-sm" data-testid="coach-review-snapshot">
                {Object.entries(a.client_snapshot).map(([k, v]) => <div key={k}><span className="text-muted-foreground capitalize">{k}:</span> {v}</div>)}
              </div>
            </Section>
          )}
          <Section title={t('steps')}>{applied || call?.evaluation_hidden ? <Bullets items={a.next_steps} accent="#C9A227" /> : <StepsEditor steps={steps} setSteps={setSteps} t={t} />}</Section>
          {!call?.evaluation_hidden && <Coaching a={a} t={t} />}
          {a.followup_message && (
            <Section title={t('followup')}>
              <div className="rounded-md border border-dashed border-[#C9A227] p-3 text-sm whitespace-pre-wrap" data-testid="coach-review-followup">{a.followup_message}</div>
              <Button size="sm" variant="outline" onClick={() => copy(a.followup_message, t)} data-testid="coach-followup-copy"><Copy className="w-3.5 h-3.5 mr-1" />{t('copy')}</Button>
            </Section>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <Button variant="ghost" onClick={onClose} data-testid="coach-review-close">{t('close')}</Button>
          {!call?.evaluation_hidden && (
            <Button onClick={save} disabled={saving || applied} className="bg-[#0F2A22] hover:bg-[#173C31]" data-testid="coach-review-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              {applied ? t('applied') : t('save')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
